import { useQuery } from "@apollo/client";
import { ODOO_COMPANY_IDS } from "AppSettings";
import Errors from "components/base/Errors";
import { InlineSpinner } from "components/base/Spinner";
import useRetryOn429 from "hooks/useRetryOn429";
import { OdooContext } from "OdooProvider";
import { Fragment, useContext } from "react";
import { FETCH_ODOO_VENDOR } from "./graphql";

const SupplierCompaniesView = ({ odooPartner }) => {
  const { odooUrl } = useContext(OdooContext);
  const { loading, error, data, refetch } = useQuery(FETCH_ODOO_VENDOR, {
    variables: {
      odooPartnerId: odooPartner.odooId,
      company: odooPartner.company,
    },
    skip: !odooPartner.odooId,
    fetchPolicy: "network-only",
  });
  const { isAutoRetrying } = useRetryOn429({ error, refetch });

  if (loading || isAutoRetrying)
    return (
      <div>
        <InlineSpinner size={16} />
      </div>
    );

  if (error) return <Errors error={error} />;

  const res = JSON.parse(data?.odooVendor || "{}");

  return (
    <div>
      <div className="flex items-center justify-between">
        <h5>
          {odooPartner.company} - {res.display_name}
        </h5>

        {odooPartner.odooId ? (
          <a
            href={`${odooUrl}/odoo/vendors/${odooPartner.odooId}?cids=${
              ODOO_COMPANY_IDS[odooPartner.company]
            }`}
            target="_blank"
            rel="noreferrer"
            className="text-purple-600"
          >
            Odoo # {odooPartner.odooId}
          </a>
        ) : (
          <div className="opacity-70 text-xs text-red-600">
            Not linked to Odoo
          </div>
        )}
      </div>
      <div className="-mx-2 mt-2">
        <table>
          <tbody>
            <tr className="border-y border-gray-100 dark:border-gray-700">
              <td>
                <label>Register Address</label>
              </td>
              <td className="w-full whitespace-pre-wrap">
                {res.contact_address}
              </td>
            </tr>
            <tr className="border-y border-gray-100 dark:border-gray-700">
              <td>
                <label>Payment Terms</label>
              </td>
              <td className="w-full">
                {res.property_supplier_payment_term_id?.display_name}
              </td>
            </tr>
            <tr className="border-y border-gray-100 dark:border-gray-700">
              <td>
                <label>Purchase Currency</label>
              </td>
              <td className="w-full">
                {res.property_purchase_currency_id?.display_name}
              </td>
            </tr>
            <tr className="border-y border-gray-100 dark:border-gray-700">
              <td>
                <label>Tax Number:</label>
              </td>
              <td className="w-full">{res.vat}</td>
            </tr>
            <tr className="border-y border-gray-100 dark:border-gray-700">
              <td>
                <label>Notes:</label>
              </td>
              <td className=" whitespace-pre-wrap">
                <div dangerouslySetInnerHTML={{ __html: res.comment || "" }} />
              </td>
            </tr>
          </tbody>
        </table>

        {res.bank_ids.length > 0 && (
          <table className="mt-4">
            <thead>
              <tr>
                <th colSpan={2}>Bank Info</th>
              </tr>
            </thead>
            <tbody>
              {res.bank_ids.map((i) => (
                <Fragment key={i.id}>
                  <tr className="border-y border-gray-100 dark:border-gray-700">
                    <td>
                      <label>Bank Name</label>
                    </td>
                    <td>{i.bank_name}</td>
                  </tr>
                  <tr className="border-y border-gray-100 dark:border-gray-700">
                    <td>
                      <label>Bank Address</label>
                    </td>
                    <td>
                      {i.bank_id?.street && <p>{i.bank_id.street}</p>}
                      {i.bank_id?.street2 && <p>{i.bank_id.street2}</p>}
                      {i.bank_id?.city && <p>{i.bank_id.city}</p>}
                      {i.bank_id?.zip && <p>{i.bank_id.zip}</p>}
                      {i.bank_id?.country && (
                        <p>{i.bank_id.country.display_name}</p>
                      )}
                    </td>
                  </tr>
                  <tr className="border-y border-gray-100 dark:border-gray-700">
                    <td>
                      <label>Account Number</label>
                    </td>
                    <td>{i.acc_number}</td>
                  </tr>
                  <tr className="border-y border-gray-100 dark:border-gray-700">
                    <td>
                      <label>SWIFT CODE</label>
                    </td>
                    <td>{i.bank_id?.bic}</td>
                  </tr>
                </Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default SupplierCompaniesView;
